import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addDays } from "date-fns";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";

import { ReactComponent as EmptyImg } from "../../assets/illustrations/empty-folder.svg";
import {
  AddWalletBanner,
  NoInvoices,
  VerifyAccountBanner,
  GetVerifiedOnmetaBanner,
} from "../../components/banners";
import {
  Button,
  FeaturedBanner,
  MultiToggle,
  MultiToggleOptionType,
} from "../../components/common";
// import { VerifyForm } from "../../components/forms/settings";
import {
  InvoicingDetailsModal,
  SelectContactModal,
} from "../../components/modals";
import { InvoicesTable } from "../../components/tables";
import { STATUS_CONFIG } from "../../constants";
import { useTriggerToastOnRTKError } from "../../hooks/useTriggerToastOnRTKError";
import DashboardLayout from "../../layouts/Dashboard.layout";
import { ROUTES } from "../../routes";
import {
  selectInvoices,
  PAGE_SIZE,
  selectInvoicesCounts,
} from "../../store/reducers/invoices";
import { setLoading, setVideoAskUrl } from "../../store/reducers/ui";
import { selectCurrentUser } from "../../store/reducers/user";
import {
  useCreateInvoiceMutation,
  // useGetInvoiceQuery,
  useGetInvoicesCountsQuery,
  useGetInvoicesQuery,
} from "../../store/services/invoices";
import { useGetWalletsQuery } from "../../store/services/wallets";
import { StatusSet } from "../../types/GlobalTypes";
import {
  // InvoiceStatus,
  InvoiceUserType,
  InvoiceListFilter,
  InvoiceCounts,
} from "../../types/InvoiceTypes";
import { formatDate, DateFormat } from "../../utils/date";
import { isEmptyObject } from "../../utils/objects";
import { isMobileMedia } from "../../utils/media";
import { KycProvider } from "../../types/UserTypes";
import { UsdAccountBanner } from "../../components/banners/UsdAccountBanner";

type Props = {
  userIs: InvoiceUserType;
};

const getNewButtonLabel = (userIs?: InvoiceUserType) => {
  switch (userIs) {
    case InvoiceUserType.ISSUER:
      return "Request payment";
    case InvoiceUserType.PAYER:
      return "New payment";
    default:
      return "New invoice";
  }
};

const getTitle = (userIs?: InvoiceUserType) => {
  switch (userIs) {
    case InvoiceUserType.ISSUER:
      return "Receive";
    case InvoiceUserType.PAYER:
      return "Pay";
    default:
      return "Invoices";
  }
};

const STATUS_PROPS = STATUS_CONFIG[StatusSet.INVOICE];

const getStatusOptions = (counts?: InvoiceCounts): MultiToggleOptionType[] => {
  return [
    {
      label: `All • ${counts?.total || 0}`,
      value: undefined,
      icon: "Arch",
    },
    ...Object.entries(STATUS_PROPS)
      // Filter out statuses without counts
      .filter(([_, { key }]) => counts && counts[key || ""] > 0)
      // Sort by order property (if exists)
      .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))
      .map(([value, { icon, text, shortText, key = "" }]) => ({
        label: `${shortText || text} • ${counts ? counts[key] : 0}`,
        value,
        icon,
      })),
  ];
};

export const InvoiceList: FC<Props> = ({ userIs }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showBoundary } = useErrorBoundary();

  const [filter, setFilter] = useState<InvoiceListFilter & { page: number }>({
    status: undefined,
    page: 1,
  });

  const { page, ...restFilter } = filter;

  const {
    id: userId,
    invoicing_data,
    kyc_complete_and_approved,
    // kyc_second_level_complete_and_approved,
    is_onmeta_eligible,
    // onmeta_kyc_required,
    onmeta_kyc_complete_and_approved,
    primary_kyc_provider,
    is_usd_eligible,
    usd_kyc_complete_and_approved,
  } = useSelector(selectCurrentUser);

  const {
    isError,
    error,
    isFetching: isFetchingInvoices,
    refetch,
  } = useGetInvoicesQuery({
    user_is: userIs,
    offset: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
    ...restFilter,
  });

  useGetInvoicesCountsQuery();
  const { data: wallets } = useGetWalletsQuery();

  if (isError) {
    showBoundary(error);
  }

  const invoicesResponse = useSelector(selectInvoices);
  const invoicesCounts = useSelector(selectInvoicesCounts);
  const [showSelectContactModal, setShowSelectContactModal] = useState(false);

  const [showAddInvoicingDetailsModal, setShowAddInvoicingDetailsModal] =
    useState(false);

  const areInvoices = invoicesResponse && invoicesResponse.results.length > 0;

  // const approvedInvoice = areInvoices
  //   ? invoicesResponse.results.find(({ status }) => [
  //     InvoiceStatus.READY,
  //     InvoiceStatus.PROCESSING,
  //     InvoiceStatus.PENDING_REVIEW,
  //     InvoiceStatus.PAID
  //   ].includes(status))
  //   : undefined;

  // const { data: fullApprovedInvoice } = useGetInvoiceQuery({
  //   id: approvedInvoice?.id || "",
  // });

  // Video guide
  useEffect(() => {
    dispatch(setVideoAskUrl("https://www.videoask.com/fm5ta096u"));

    return () => {
      dispatch(setVideoAskUrl(null));
    };
  }, [dispatch]);

  useEffect(() => {
    refetch();
  }, [page, refetch, userIs]);

  useEffect(() => {
    dispatch(setLoading(isFetchingInvoices));

    return () => {
      dispatch(setLoading(false));
    };
  }, [dispatch, isFetchingInvoices]);

  // TODO: Refactor this logic to a separate modal component to create invoices
  // from contact modal
  const [
    createInvoice,
    {
      data: newInvoice,
      isSuccess,
      isError: isErrorCreateInvoice,
      error: errorCreateInvoice,
    },
  ] = useCreateInvoiceMutation();

  useTriggerToastOnRTKError(isErrorCreateInvoice, errorCreateInvoice);

  useEffect(() => {
    if (isSuccess) {
      newInvoice && navigate(ROUTES.INVOICE.replace(":id", newInvoice.id));
    }
  }, [isSuccess, navigate, newInvoice]);

  const createInvoiceHandler = async () => {
    if (userIs === InvoiceUserType.ISSUER) {
      await createInvoice({
        issuer_id: userId,
        issued_at: formatDate(new Date().toString(), DateFormat.yyyy_MM_dd),
        due_at: formatDate(
          addDays(new Date(), 14).toString(),
          DateFormat.yyyy_MM_dd
        ),
      });
    } else {
      setShowSelectContactModal(true);
    }
  };

  const onChangePage = useCallback((newPage: number) => {
    setFilter((current) => ({ ...current, page: newPage }));
  }, []);

  const statusOptions = useMemo(
    () => getStatusOptions(invoicesCounts),
    [invoicesCounts]
  );

  const NewInvoiceBtn = (
    <Button
      className="w-full md:w-auto"
      variant={Button.variant.TEMPTING}
      onClick={createInvoiceHandler}
      data-test="new-invoice-btn"
    >
      {getNewButtonLabel(userIs)}
    </Button>
  );

  // const showVerifyForm = useVerifyForm(approvedInvoice);

  const isVerified = kyc_complete_and_approved;

  const isOnmetaVerified = onmeta_kyc_complete_and_approved;
  // Currently, only Indian businesses have Sumsub has primary provider.
  const shouldShowOnmetaKyc =
    is_onmeta_eligible && primary_kyc_provider !== KycProvider.ONMETA;

  const shouldShowUsdBanner = is_usd_eligible && !usd_kyc_complete_and_approved;

  const hasWallets = wallets && wallets.results.length > 0;

  return (
    <DashboardLayout
      title={
        <div className="flex flex-col gap-6">
          {getTitle(userIs)}

          {areInvoices && !isMobileMedia() && (
            <MultiToggle
              name="invoice-status-filter"
              options={statusOptions}
              value={filter.status}
              onChange={(status) =>
                setFilter((current) => ({ ...current, status, page: 1 }))
              }
            />
          )}
        </div>
      }
      containerClassName="md:px-4"
      action={NewInvoiceBtn}
      onScrollReachedBottomHandler={() => {
        if (invoicesResponse && invoicesResponse.results.length >= 2) {
          onChangePage(page + 1);
        }
      }}
      hideIntercomOnInit={true}
    >
      {areInvoices ? (
        <>
          <InvoicesTable data={invoicesResponse?.results} userIs={userIs} />

          {/* {showVerifyForm && (
            <div className="mt-8 md:w-[337px]">
              <VerifyForm invoice={fullApprovedInvoice} className="mb-12" />
            </div>
          )} */}
        </>
      ) : (
        <div className="flex flex-col gap-y-12">
          {!invoicing_data || isEmptyObject(invoicing_data) ? (
            <FeaturedBanner
              title="Add your details to issue invoices & get payments"
              description="Just fill them out once, and we'll take care of the rest by automatically filling the invoices"
              action={{
                label: "Add details",
                onClick: () => setShowAddInvoicingDetailsModal(true),
              }}
              className="bg-purple-pattern-blended h-[324px]"
              rightPlaceholder={
                <div className="flex items-center h-full pr-10">
                  <EmptyImg className="w-[240px]" />
                </div>
              }
            />
          ) : (
            <NoInvoices createInvoiceHandler={createInvoiceHandler} />
          )}
        </div>
      )}

      {(!hasWallets ||
        !isVerified ||
        (!isOnmetaVerified && shouldShowOnmetaKyc) ||
        shouldShowUsdBanner) && (
        <div className="pt-6 md:pt-10 flex flex-col gap-3 md:gap-12">
          <h3 className="font-poppins font-bold text-xl md:text-3xl">
            Get started with Archway
          </h3>

          <div className="flex overflow-x-auto w-full">
            <div className="flex gap-6 min-w-[150vw] md:min-w-full pr-4">
              {!hasWallets && <AddWalletBanner />}
              {!isVerified && <VerifyAccountBanner />}

              {
                //onmeta_kyc_required &&
                shouldShowOnmetaKyc && !isOnmetaVerified && (
                  <GetVerifiedOnmetaBanner />
                )
              }
              {shouldShowUsdBanner && <UsdAccountBanner />}
            </div>
          </div>
        </div>
      )}

      {showAddInvoicingDetailsModal && (
        <InvoicingDetailsModal
          isOpen
          onClose={() => setShowAddInvoicingDetailsModal(false)}
        />
      )}

      {showSelectContactModal && (
        <SelectContactModal
          userIs={userIs}
          isOpen={showSelectContactModal}
          onClose={() => setShowSelectContactModal(false)}
        />
      )}
    </DashboardLayout>
  );
};
